import type { EnumUnion } from '@dresscode-frontend/utility-types';
import { match } from 'ts-pattern';

import type { BadgeVariant } from '@/components/Badge';
import type { IconName } from '@/components/Icon/iconMap';

import {
  type EquipmentItemType,
  type EquipmentStatus,
  EquipmentItemType as EIT,
  EquipmentStatus as ES,
} from './type';
import type { EquipmentListItem } from './type';

type EquipmentTableEquipmentColumn = Pick<
  EquipmentListItem,
  'id' | 'name' | 'equipmentTypeName'
> & { avatarUrl?: string };

type EquipmentTypeInfo = {
  typeName: string;
  itemName?: string;
};

type Person = {
  id?: string;
  name?: string;
  avatarUrl?: string;
  email?: string;
};

export const EquipmentTableColumns = {
  EQUIPMENT: 'EQUIPMENT',
  EQUIPMENT_TYPE: 'EQUIPMENT_TYPE',
  ITEM_TYPE: 'ITEM_TYPE',
  MODEL_NUMBER: 'MODEL_NUMBER',
  PRODUCT_NAME: 'PRODUCT_NAME',
  MANUFACTURER: 'MANUFACTURER',
  SERIAL_NUMBER: 'SERIAL_NUMBER',
  ASSET_TAG: 'ASSET_TAG',
  EQUIPMENT_STATUS: 'EQUIPMENT_STATUS',
  EQUIPMENT_STATUS_LABEL: 'EQUIPMENT_STATUS_LABEL',
  ASSIGNED_PERSON: 'ASSIGNED_PERSON',
  ASSIGNED_PLACE: 'ASSIGNED_PLACE',
  ASSIGNMENT_START_DATE: 'ASSIGNMENT_START_DATE',
  EXPECTED_RETURN_DATE: 'EXPECTED_RETURN_DATE',
  PURCHASE_DATE: 'PURCHASE_DATE',
  PLACED_LOCATION: 'PLACED_LOCATION',
  PLACED_DATE: 'PLACED_DATE',
  FAILURE_DATE: 'FAILURE_DATE',
  FAILURE_DESCRIPTION: 'FAILURE_DESCRIPTION',
  DISPOSAL_DATE: 'DISPOSAL_DATE',
} as const;
export type EquipmentTableColumns = EnumUnion<typeof EquipmentTableColumns>;

export type EquipmentTableRowData = {
  [EquipmentTableColumns.EQUIPMENT]: EquipmentTableEquipmentColumn;
  [EquipmentTableColumns.EQUIPMENT_TYPE]: EquipmentTypeInfo;
  [EquipmentTableColumns.ITEM_TYPE]: EquipmentItemType;
  [EquipmentTableColumns.MODEL_NUMBER]: string | undefined;
  [EquipmentTableColumns.PRODUCT_NAME]: string | undefined;
  [EquipmentTableColumns.MANUFACTURER]: string | undefined;
  [EquipmentTableColumns.SERIAL_NUMBER]: string | undefined;
  [EquipmentTableColumns.ASSET_TAG]: string | undefined;
  [EquipmentTableColumns.EQUIPMENT_STATUS]: EquipmentStatus | undefined;
  [EquipmentTableColumns.EQUIPMENT_STATUS_LABEL]: string | undefined;
  [EquipmentTableColumns.ASSIGNED_PERSON]: Person | null;
  [EquipmentTableColumns.ASSIGNED_PLACE]: string | undefined;
  [EquipmentTableColumns.ASSIGNMENT_START_DATE]: string | null | undefined;
  [EquipmentTableColumns.EXPECTED_RETURN_DATE]: string | null | undefined;
  [EquipmentTableColumns.PURCHASE_DATE]: string | undefined;
  [EquipmentTableColumns.PLACED_LOCATION]: string | undefined;
  [EquipmentTableColumns.PLACED_DATE]: string | undefined;
  [EquipmentTableColumns.FAILURE_DATE]: string | undefined;
  [EquipmentTableColumns.FAILURE_DESCRIPTION]: string | undefined;
  [EquipmentTableColumns.DISPOSAL_DATE]: string | undefined;
};

export function getEquipmentStatusBadgeVariant(
  status: EquipmentStatus,
): BadgeVariant {
  return match<EquipmentStatus, BadgeVariant>(status)
    .with(ES.AVAILABLE, () => 'mutedSuccess')
    .with(ES.ASSIGNED, () => 'primaryMuted')
    .with(ES.PLACED, () => 'complementaryMuted')
    .with(ES.UNDER_REPAIR, () => 'mutedWarning')
    .with(ES.DECOMMISSIONED, () => 'neutralMuted')
    .exhaustive();
}

export function getEquipmentItemTypeBadgeIcon(
  itemType: EquipmentItemType,
): IconName {
  return match<EquipmentItemType, IconName>(itemType)
    .with(EIT.LOANABLE, () => 'UserRoundCheckIcon')
    .with(EIT.TRANSFERABLE, () => 'GiftIcon')
    .with(EIT.CONSUMABLE, () => 'LoaderIcon')
    .exhaustive();
}

export const convertEquipmentItemToTableRow = (
  item: EquipmentListItem,
): EquipmentTableRowData => {
  return {
    [EquipmentTableColumns.EQUIPMENT]: {
      id: item.id,
      name: item.name,
      equipmentTypeName: item.equipmentTypeName,
      avatarUrl: item.avatarUrl,
    },
    [EquipmentTableColumns.EQUIPMENT_TYPE]: {
      typeName: item.equipmentTypeName,
      itemName: item.equipmentItemName,
    },
    [EquipmentTableColumns.ITEM_TYPE]: item.itemType,
    [EquipmentTableColumns.MODEL_NUMBER]: item.modelNumber ?? undefined,
    [EquipmentTableColumns.PRODUCT_NAME]: item.productName ?? undefined,
    [EquipmentTableColumns.MANUFACTURER]: item.manufacturer ?? undefined,
    [EquipmentTableColumns.SERIAL_NUMBER]: item.serialNumber,
    [EquipmentTableColumns.ASSET_TAG]: item.assetTag,
    [EquipmentTableColumns.EQUIPMENT_STATUS]: item.equipmentStatus ?? undefined,
    [EquipmentTableColumns.EQUIPMENT_STATUS_LABEL]: undefined,
    [EquipmentTableColumns.ASSIGNED_PERSON]: item.assignedPerson
      ? {
          id: item.assignedPerson.personId,
          name: item.assignedPerson.name,
          avatarUrl: item.assignedPerson.avatarUrl,
          email: undefined,
        }
      : null,
    [EquipmentTableColumns.ASSIGNED_PLACE]: item.assignedPlace?.placeLabel,
    [EquipmentTableColumns.ASSIGNMENT_START_DATE]: item.assignmentStartDate,
    [EquipmentTableColumns.EXPECTED_RETURN_DATE]: item.expectedReturnDate,
    [EquipmentTableColumns.PURCHASE_DATE]: item.purchaseDate,
    [EquipmentTableColumns.PLACED_LOCATION]: item.placedLocation ?? undefined,
    [EquipmentTableColumns.PLACED_DATE]: item.placedDate ?? undefined,
    [EquipmentTableColumns.FAILURE_DATE]: item.failureDate ?? undefined,
    [EquipmentTableColumns.FAILURE_DESCRIPTION]:
      item.failureDescription ?? undefined,
    [EquipmentTableColumns.DISPOSAL_DATE]: item.disposalDate ?? undefined,
  };
};
